import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Column } from '../Common/Grid';
import OrderSummary from './OrderSummary';
import OrderTotalSummary from './OrderTotalSummary';
import OrderDetailsCTAGroup from './OrderDetailsCTAGroup';
import OrderItemsContainer from './OrderItemsContainer';
import WssPaymentAuthorizationSection from './WssPaymentAuthorizationSection';
import OrderDetailsRecommendations from './OrderDetailsRecommendations';
import WssOrderDetailsSection from './WssOrderDetailsSection';
import CancelOrderModal from '../OrderHistory/CancelOrderModalContainer';
import { orderDetailsDataProps, orderDetailsDataDefaultProps } from './props';
import { decodeHtmlEntities } from '../../tools/html/decode';

export default function OrderDetailsContent({ queryData, onPaymentError, onPaymentSuccess }) {
  const [orderNumber, setOrderNumber] = useState('');
  const [orderEmail, setOrderEmail] = useState('');
  const [isCancelItemOpen, setIsCancelItemOpen] = useState(false);
  const [cancelItemShortSku, setCancelItemShortSku] = useState(undefined);

  useEffect(() => {
    // Extract order number from URL parameters, normalizing malformed URLs
    const currentHref = decodeHtmlEntities(window.location.href);
    const url = new URL(currentHref);
    const searchParams = new URLSearchParams(url.search);
    let orderId = searchParams.get('orderNumber');
    let emailParam = searchParams.get('orderEmail');

    // If hash holds params due to a bad rewrite, recover them
    if ((!orderId || !emailParam) && url.hash) {
      const hash = decodeHtmlEntities(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash);
      const hashParams = new URLSearchParams(hash.replace(/^\?/, ''));
      orderId = orderId || hashParams.get('orderNumber') || hashParams.get('ordernumber');
      emailParam = emailParam || hashParams.get('orderEmail') || hashParams.get('orderemail');
    }

    // Restore from sessionStorage if still missing
    if (!orderId || (!emailParam && !searchParams.get('channel'))) {
      try {
        const savedRaw = window.sessionStorage.getItem('orderDetailsParams');
        if (savedRaw) {
          const saved = JSON.parse(savedRaw);
          orderId = orderId || saved?.orderNumber || saved?.orderId;
          emailParam = emailParam || saved?.orderEmail || saved?.email;
        }
      } catch {
        // no-op
      }
    }

    if (orderId) setOrderNumber(orderId);
    if (emailParam) setOrderEmail(emailParam);

    // Normalize the URL if we reconstructed params
    if (orderId) {
      const newParams = new URLSearchParams(url.search);
      newParams.set('orderNumber', orderId);
      if (emailParam) newParams.set('orderEmail', emailParam);
      const newUrl = `${url.pathname}?${newParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const { detail } = event || {};
      if (detail?.cancel === 'item') {
        setCancelItemShortSku(detail.shortSku || undefined);
        setIsCancelItemOpen(true);
      }
    };
    document.addEventListener('cancel-item-modal:open', handler);
    return () => document.removeEventListener('cancel-item-modal:open', handler);
  }, []);

  const orderDetailItems = queryData?.orderDetails?.orderDetailItems || {};

  const {
    billingInfo,
    shippingDetails,
    vatDutyDetails,
    summary,
    ctaGroup,
    products,
    packages,
    wssPaymentDetails,
    heading,
    status,
    currency,
    currencySymbol,
  } = orderDetailItems;

  const {
    pleaseTryAgain,
    processingText,
    successText,
  } = queryData?.textFor || {};

  return (
    <div className="order-details-content" data-testid="order-details-content">
      {wssPaymentDetails && wssPaymentDetails.paymentUpdateNeeded && (
        <WssPaymentAuthorizationSection
          orderNumber={orderNumber}
          wssPaymentDetails={wssPaymentDetails}
          onPaymentError={onPaymentError}
          onPaymentSuccess={onPaymentSuccess}
          currency={currency}
          currencySymbol={currencySymbol}
        />
      )}
      <WssOrderDetailsSection
        heading={heading}
        status={status}
        hasWssPaymentDetails={wssPaymentDetails && wssPaymentDetails.paymentUpdateNeeded}
      >
        <Row>
          { summary
          && (
          <Column md="4">
            <div className="order-total-summary" data-testid="order-total-summary">
              <OrderTotalSummary summary={summary} />
            </div>
          </Column>
          )}
          { ctaGroup && (
          <Column md="4">
            <div className="order-details-cta-group" data-testid="order-details-cta-group">
              <OrderDetailsCTAGroup
                ctaGroupButtons={{
                  ctaGroup,
                  number: orderNumber,
                  resendOrder: {
                    number: ctaGroup[0]?.value,
                    buttonText: ctaGroup[0]?.label,
                    processing: processingText,
                    error: pleaseTryAgain,
                    success: successText,
                  },
                }}
              />
            </div>
          </Column>
          )}
        </Row>
        <hr />
        <OrderItemsContainer
          orderProducts={products || []}
          packages={packages || []}
          text={queryData?.textFor}
          currency={currency}
          currencySymbol={currencySymbol}
        />
        <OrderDetailsRecommendations orderId={orderNumber} />
        <OrderSummary
          shippingDetails={shippingDetails}
          billingInfo={billingInfo}
          vatDutyDetails={vatDutyDetails}
        />
      </WssOrderDetailsSection>
      {/* Item-level Cancel Modal (opened via custom event from ItemActions) */}
      {isCancelItemOpen && (
        <CancelOrderModal
          isOpen={isCancelItemOpen}
          onClose={() => setIsCancelItemOpen(false)}
          orderNumber={orderNumber}
          email={orderEmail}
          itemShortSku={cancelItemShortSku}
        />
      )}
    </div>
  );
}

OrderDetailsContent.propTypes = {
  ...orderDetailsDataProps,
  onPaymentError: PropTypes.func,
  onPaymentSuccess: PropTypes.func,
};
OrderDetailsContent.defaultProps = {
  ...orderDetailsDataDefaultProps,
  onPaymentError: undefined,
  onPaymentSuccess: undefined,
};
